/**
 * The one way a page asks "what is this sticker?": POST /scanner/lookup, with
 * the stale-answer guard and the miss wording. /parts/lookup opens the answer's
 * page; the scanner's Lookup tool renders it in place. Both read it here so they
 * cannot disagree about what a miss means.
 */

import { useCallback, useRef, useState } from 'react';
import { useApi } from '../apiHelpers';
import type { ScanLookupResult } from './scanDestination';

export type LookupMiss = 'not_found' | 'error';

export const LOOKUP_MISS_TEXT: Record<LookupMiss, string> = {
  not_found: 'Nothing registered under that code.',
  error: 'Could not look that up. Check your connection and scan again.',
};

export type LookupOutcome =
  | { status: 'found'; result: ScanLookupResult }
  | { status: 'miss'; miss: LookupMiss; httpStatus: number | null }
  /** Empty input, or superseded by a later lookup or an edit. Do nothing with it. */
  | { status: 'ignored' };

export function useStickerLookup() {
  const api = useApi();
  const [resolving, setResolving] = useState(false);
  const [miss, setMiss] = useState<LookupMiss | null>(null);
  // Re-keys the miss line so a second miss in a row lands again instead of
  // sitting still, which would read as the scan not registering.
  const [missCount, setMissCount] = useState(0);
  // A lookup that lands after the field has moved on must not be acted on.
  const generation = useRef(0);

  const markMiss = useCallback((kind: LookupMiss) => {
    setMiss(kind);
    setMissCount((n) => n + 1);
  }, []);

  /** Supersede any lookup in flight and clear the miss, e.g. on an edit. */
  const cancel = useCallback(() => {
    generation.current += 1;
    setResolving(false);
    setMiss(null);
  }, []);

  const lookup = useCallback(async (raw: string): Promise<LookupOutcome> => {
    const code = raw.trim();
    if (!code) return { status: 'ignored' };
    const mine = ++generation.current;
    setMiss(null);
    setResolving(true);
    try {
      const result = (await api.post('/scanner/lookup', { body: { raw: code } })) as ScanLookupResult;
      if (mine !== generation.current) return { status: 'ignored' };
      return { status: 'found', result };
    } catch (err: any) {
      if (mine !== generation.current) return { status: 'ignored' };
      const kind: LookupMiss = err?.status === 404 ? 'not_found' : 'error';
      markMiss(kind);
      return { status: 'miss', miss: kind, httpStatus: err?.status ?? null };
    } finally {
      if (mine === generation.current) setResolving(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { lookup, cancel, markMiss, resolving, miss, missCount };
}
